import { useCallback, useMemo } from "react";
import { SelectFieldOption } from "../../beta/components/SelectField/types";
import { NormalizedPinnedSection } from "./usePinnedOptions";

type UseToggleSelectionParams = {
  options: SelectFieldOption[];
  pinnedSections: NormalizedPinnedSection[];
  selectedOptions: SelectFieldOption[];
  onSelectedOptionsChange: (options: SelectFieldOption[]) => void;
};

/**
 * Shared multi-select toggle logic: computes allPinnedOptions and provides
 * handleItemClick to add/remove an option from the selection.
 * Used by both useComboMultiple and useSelectModeMultiple.
 */
export const useToggleSelection = ({
  options,
  pinnedSections,
  selectedOptions,
  onSelectedOptionsChange,
}: UseToggleSelectionParams) => {
  const allPinnedOptions = useMemo(() => {
    return pinnedSections.flatMap((section) => section.options);
  }, [pinnedSections]);

  const handleItemClick = useCallback(
    (option: { id: string | number; label: string }) => {
      const isSelected = selectedOptions.some((opt) => opt.id === option.id);
      if (isSelected) {
        onSelectedOptionsChange(
          selectedOptions.filter((opt) => opt.id !== option.id),
        );
      } else {
        const fullOption =
          options.find((opt) => opt.id === option.id) ||
          allPinnedOptions.find((opt) => opt.id === option.id);
        if (fullOption) {
          onSelectedOptionsChange([...selectedOptions, fullOption]);
        }
      }
    },
    [selectedOptions, onSelectedOptionsChange, options, allPinnedOptions],
  );

  return { handleItemClick, allPinnedOptions };
};
